<template>
  <div class="geolocation-instructions">
    <div class="instruction-modal" v-if="isVisible">
      <div class="instruction-content">
        <div class="instruction-header">
          <h2>{{ title }}</h2>
          <vs-button type="flat" icon="close" @click="close" class="close-button"></vs-button>
        </div>

        <div class="instruction-body">
          <!-- Instrucciones según el navegador -->
          <div v-if="browser === 'chrome'">
            <h3>Para habilitar el acceso a su ubicación en Chrome:</h3>
            <div class="steps">
              <div class="step">
                <div class="step-number">1</div>
                <div class="step-text">
                  Haga clic en el ícono de permisos del sitio (círculo pequeño) a la izquierda de la URL en la barra de direcciones
                </div>
              </div>
              <div class="step">
                <div class="step-number">2</div>
                <div class="step-text">
                  En el menú desplegable, busque "Ubicación" y cambie la configuración de "Bloqueado" a "Permitir"
                </div>
              </div>
              <div class="step">
                <div class="step-number">3</div>
                <div class="step-text">Recargue la página haciendo clic en el botón de recarga o presionando F5</div>
              </div>
            </div>
          </div>

          <div v-else-if="browser === 'edge'">
            <h3>Para habilitar el acceso a su ubicación en Microsoft Edge:</h3>
            <div class="steps">
              <div class="step">
                <div class="step-number">1</div>
                <div class="step-text">
                  Haga clic en el ícono de permisos del sitio (círculo pequeño) a la izquierda de la URL en la barra de direcciones
                </div>
              </div>
              <div class="step">
                <div class="step-number">2</div>
                <div class="step-text">
                  En el menú desplegable, busque "Ubicación" y cambie la configuración de "Bloqueado" a "Permitir"
                </div>
              </div>
              <div class="step">
                <div class="step-number">3</div>
                <div class="step-text">Recargue la página</div>
              </div>
            </div>
          </div>

          <div v-else-if="browser === 'firefox'">
            <h3>Para habilitar el acceso a su ubicación en Firefox:</h3>
            <div class="steps">
              <div class="step">
                <div class="step-number">1</div>
                <div class="step-text">
                  Haga clic en el ícono del escudo o candado a la izquierda de la URL en la barra de direcciones
                </div>
              </div>
              <div class="step">
                <div class="step-number">2</div>
                <div class="step-text">En el panel que aparece, haga clic en la opción "Conexión segura"</div>
              </div>
              <div class="step">
                <div class="step-number">3</div>
                <div class="step-text">
                  Busque "Acceder a su ubicación" y use el menú desplegable para seleccionar "Permitir"
                </div>
              </div>
              <div class="step">
                <div class="step-number">4</div>
                <div class="step-text">Recargue la página</div>
              </div>
            </div>
          </div>

          <div v-else-if="browser === 'safari'">
            <h3>Para habilitar el acceso a su ubicación en Safari:</h3>
            <div class="steps">
              <div class="step">
                <div class="step-number">1</div>
                <div class="step-text">Haga clic en "Safari" en la barra de menú superior</div>
              </div>
              <div class="step">
                <div class="step-number">2</div>
                <div class="step-text">Seleccione "Ajustes para este sitio web"</div>
              </div>
              <div class="step">
                <div class="step-number">3</div>
                <div class="step-text">
                  En el menú desplegable junto a "Servicios de ubicación", seleccione "Permitir"
                </div>
              </div>
              <div class="step">
                <div class="step-number">4</div>
                <div class="step-text">Vuelva a la página y recárguela</div>
              </div>
            </div>
          </div>

          <div v-else>
            <h3>Para habilitar el acceso a su ubicación:</h3>
            <div class="steps">
              <div class="step">
                <div class="step-number">1</div>
                <div class="step-text">
                  Haga clic en el ícono de permisos del sitio (círculo pequeño o candado) en la barra de direcciones
                </div>
              </div>
              <div class="step">
                <div class="step-number">2</div>
                <div class="step-text">
                  Busque la opción relacionada con "Ubicación" o "Permisos de ubicación"
                </div>
              </div>
              <div class="step">
                <div class="step-number">3</div>
                <div class="step-text">Cambie la configuración a "Permitir"</div>
              </div>
              <div class="step">
                <div class="step-number">4</div>
                <div class="step-text">Recargue la página e intente nuevamente</div>
              </div>
            </div>
          </div>

          <!-- Instrucciones para dispositivos móviles si aplica -->
          <div v-if="isMobile" class="mobile-instructions">
            <h3 class="mt-4">Instrucciones para dispositivos móviles:</h3>
            <div class="steps">
              <div v-if="browser === 'chrome' && isAndroid" class="step-group">
                <h4>Chrome en Android:</h4>
                <div class="step">
                  <div class="step-number">1</div>
                  <div class="step-text">Toque los tres puntos ⋮ en la esquina superior derecha</div>
                </div>
                <div class="step">
                  <div class="step-number">2</div>
                  <div class="step-text">Toque en "Configuración del sitio"</div>
                </div>
                <div class="step">
                  <div class="step-number">3</div>
                  <div class="step-text">Toque en "Ubicación"</div>
                </div>
                <div class="step">
                  <div class="step-number">4</div>
                  <div class="step-text">Seleccione "Permitir"</div>
                </div>
              </div>

              <div v-if="browser === 'safari' && isIOS" class="step-group">
                <h4>Safari en iOS:</h4>
                <div class="step">
                  <div class="step-number">1</div>
                  <div class="step-text">Vaya a la app "Ajustes" de su dispositivo</div>
                </div>
                <div class="step">
                  <div class="step-number">2</div>
                  <div class="step-text">Desplácese hacia abajo y toque "Safari"</div>
                </div>
                <div class="step">
                  <div class="step-number">3</div>
                  <div class="step-text">Toque en "Servicios de localización"</div>
                </div>
                <div class="step">
                  <div class="step-number">4</div>
                  <div class="step-text">Asegúrese de que "Safari" esté configurado como "Al usar la app"</div>
                </div>
              </div>

              <div class="step-group">
                <h4>Configuración del sistema:</h4>
                <div class="step">
                  <div class="step-number">1</div>
                  <div class="step-text">Verifique que la ubicación esté activada en la configuración general de su dispositivo</div>
                </div>
                <div class="step">
                  <div class="step-number">2</div>
                  <div class="step-text">Si utiliza Android, vaya a Configuración → Ubicación y asegúrese de que esté habilitada</div>
                </div>
                <div class="step">
                  <div class="step-number">3</div>
                  <div class="step-text">Si utiliza iOS, vaya a Configuración → Privacidad → Servicios de localización y asegúrese de que esté habilitada</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="instruction-footer">
          <vs-button @click="reloadPage" type="filled" color="primary">
            Recargar página
          </vs-button>
          <vs-button @click="close" type="border" class="ml-3">
            Cerrar
          </vs-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GeolocationInstructions',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    browser: {
      type: String,
      default: 'other'
    },
    isMobile: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: 'Cómo habilitar la ubicación'
    }
  },
  computed: {
    isAndroid() {
      return /Android/i.test(navigator.userAgent);
    },
    isIOS() {
      return /iPhone|iPad|iPod/i.test(navigator.userAgent);
    }
  },
  methods: {
    close() {
      this.$emit('close');
    },
    reloadPage() {
      this.$emit('reload');
    }
  }
}
</script>

<style scoped>
.geolocation-instructions {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

.instruction-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.instruction-content {
  background-color: white;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
}

.instruction-header {
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.instruction-header h2 {
  margin: 0;
  font-size: 1.5rem;
  color: #333;
}

.close-button {
  min-width: auto;
  min-height: auto;
}

.instruction-body {
  padding: 20px;
}

.instruction-body h3 {
  margin-top: 0;
  margin-bottom: 16px;
  color: #444;
  font-size: 1.3rem;
}

.instruction-body h4 {
  margin-top: 12px;
  margin-bottom: 10px;
  color: #555;
  font-size: 1.1rem;
}

.steps {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.step-group {
  margin-bottom: 20px;
}

.step {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}

.step-number {
  background-color: #4173FF;
  color: white;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
  flex-shrink: 0;
}

.step-text {
  flex-grow: 1;
  padding-top: 4px;
  line-height: 1.5;
}

.mobile-instructions {
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.instruction-footer {
  padding: 16px 20px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
}

.ml-3 {
  margin-left: 12px;
}

.mt-4 {
  margin-top: 16px;
}
</style>
